/**
 * EditShareLinkSheet Component (Story 14.2 + Story 14.4)
 *
 * Right-side sheet for editing the mutable metadata of a portal share link
 * (label, expiration, hidden metrics, hidden integrations).
 *
 * Scope, reportId, password, and the token itself are immutable post-create —
 * to change them, the agency must revoke and re-create. This is documented in
 * 14.2 AC #4 and enforced server-side via Zod .strict().
 *
 * Story 14.4 — The hidden-filter controls are rendered by
 * `PortalAccessControlsCard`. Initial state is parsed from the link's
 * `hiddenMetricsJson` / `hiddenIntegrationsJson` projection fields, and the
 * dirty-check uses `arraysEqualConsideringNullInitial` to avoid sending stale
 * PATCH bodies when the user never touched the card (null → [] is NOT dirty).
 */

import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button, Input } from "@provenleap/ui";
import { motion, AnimatePresence } from "motion/react";
import {
  updatePortalLink,
  parsePortalHiddenArray,
  arraysEqualConsideringNullInitial,
  type PortalLinkExpirationPreset,
  type PortalLinkProjection,
  type UpdatePortalLinkInput,
} from "../../lib/portalLinksApi";
import type { ToastType } from "../../hooks/useToast";
import { PortalAccessControlsCard } from "./PortalAccessControlsCard";
import { SelectPicker } from "./SelectPicker";

interface EditShareLinkSheetProps {
  link: PortalLinkProjection;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
  showToast: (type: ToastType, message: string) => void;
}

type ExpirationChoice = PortalLinkExpirationPreset | "no-change";

const EXPIRATION_OPTIONS: Array<{ value: ExpirationChoice; label: string }> = [
  { value: "no-change", label: "Keep current expiration" },
  { value: "7d", label: "7 days from now" },
  { value: "30d", label: "30 days from now" },
  { value: "90d", label: "90 days from now" },
  { value: "never", label: "Never expires" },
];

export function EditShareLinkSheet({
  link,
  open,
  onOpenChange,
  onSaved,
  showToast,
}: EditShareLinkSheetProps) {
  const [label, setLabel] = useState(link.label);
  const [expirationChoice, setExpirationChoice] =
    useState<ExpirationChoice>("no-change");
  const [hiddenMetrics, setHiddenMetrics] = useState<string[]>(() =>
    parsePortalHiddenArray(link.hiddenMetricsJson)
  );
  const [hiddenIntegrations, setHiddenIntegrations] = useState<string[]>(() =>
    parsePortalHiddenArray(link.hiddenIntegrationsJson)
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setLabel(link.label);
      setExpirationChoice("no-change");
      setHiddenMetrics(parsePortalHiddenArray(link.hiddenMetricsJson));
      setHiddenIntegrations(parsePortalHiddenArray(link.hiddenIntegrationsJson));
      setErrors({});
    }
  }, [open, link]);

  // Story 14.4: treat `null → []` as NOT dirty so unchanged arrays don't churn
  // `updated_at` server-side.
  const hiddenMetricsDirty = !arraysEqualConsideringNullInitial(
    link.hiddenMetricsJson,
    hiddenMetrics
  );
  const hiddenIntegrationsDirty = !arraysEqualConsideringNullInitial(
    link.hiddenIntegrationsJson,
    hiddenIntegrations
  );
  const isDirty =
    label.trim() !== link.label ||
    expirationChoice !== "no-change" ||
    hiddenMetricsDirty ||
    hiddenIntegrationsDirty;

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (label.trim().length === 0) next.label = "Label is required";
    if (label.length > 120) next.label = "Label must be 120 characters or less";
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  const update = useMutation({
    mutationFn: () => {
      const input: UpdatePortalLinkInput = {};
      if (label.trim() !== link.label) {
        input.label = label.trim();
      }
      if (expirationChoice !== "no-change") {
        input.expiresAt = expirationChoice;
      }
      // Only send arrays when the user actually changed them — prevents
      // needless `updated_at` bumps on unchanged filter state.
      if (hiddenMetricsDirty) {
        input.hiddenMetrics = hiddenMetrics;
      }
      if (hiddenIntegrationsDirty) {
        input.hiddenIntegrations = hiddenIntegrations;
      }
      return updatePortalLink(link.id, input);
    },
    onSuccess: () => onSaved(),
    onError: (err: Error) => {
      showToast("error", err.message ?? "Failed to update share link");
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    update.mutate();
  }

  // Block close while submitting (avoids losing in-flight state).
  function handleOpenChange(next: boolean): void {
    if (!next && update.isPending) return;
    onOpenChange(next);
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex flex-col space-y-1.5 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    Edit share link
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    Update the label or extend the expiration. To change scope or
                    password, revoke this link and create a new one.
                  </DialogPrimitive.Description>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 mt-6">
          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <label htmlFor="edit-label" className="text-sm font-medium">
              Label <span className="text-destructive">*</span>
              {errors.label && (
                <span className="text-sm text-destructive font-normal ml-1" role="alert">
                  {errors.label}
                </span>
              )}
            </label>
            <Input
              id="edit-label"
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
                if (errors.label) setErrors((prev) => ({ ...prev, label: "" }));
              }}
              className={`rounded-lg ${errors.label ? "border-destructive" : ""}`}
              maxLength={120}
              data-testid="edit-label-input"
              autoFocus
            />
          </motion.div>

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <label htmlFor="edit-expires" className="text-sm font-medium">
              Expiration
            </label>
            <SelectPicker
              id="edit-expires"
              value={expirationChoice}
              onChange={(v) => setExpirationChoice(v as ExpirationChoice)}
              options={EXPIRATION_OPTIONS}
              testId="edit-expires-select"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <PortalAccessControlsCard
              clientId={link.clientId}
              hiddenMetrics={hiddenMetrics}
              hiddenIntegrations={hiddenIntegrations}
              onChange={(next) => {
                setHiddenMetrics(next.hiddenMetrics);
                setHiddenIntegrations(next.hiddenIntegrations);
              }}
              disabled={update.isPending}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
              <Button
                type="button"
                variant="ghost"
                className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                style={{ border: "0.5px solid hsl(var(--input))" }}
                onClick={() => onOpenChange(false)}
                disabled={update.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="brand"
                className="rounded-xl cursor-pointer"
                disabled={update.isPending || !isDirty}
                data-testid="submit-edit-link"
              >
                {update.isPending ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </motion.div>
                </form>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
